import { useState, type FormEvent, type ReactNode } from 'react';
import type { z } from 'zod';
import { ConfirmButton } from '../../components/confirm';
import { NoticeField } from '../../components/notice-field';
import { Field } from '../../components/page';
import { ActionError, Empty, QueryView } from '../../components/states';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog } from '../../components/ui/dialog';
import { Input, Select, Textarea } from '../../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table';
import { useApi, useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { useApiMutation, useApiQuery } from '../../lib/query';
import { fieldErrors } from '../portfolio-forms';

// Référentiel éditable générique (assureurs, lignes de produit, agents) : table, création et
// modification dans une fenêtre, suppression confirmée. Validation par les schémas partagés.

export interface FieldSpec {
  /** Clé du corps ; `a.b` écrit dans l'objet imbriqué `a`. */
  key: string;
  label: string;
  hint?: string;
  /** `boolean` : oui, non ou vide (réglage hérité) ; `notice` : délai en jours (1, 2, 3 mois ou libre). */
  type?: 'text' | 'number' | 'select' | 'textarea' | 'boolean' | 'notice';
  /** Libellé du choix vide d'un délai (`notice`). */
  none?: string;
  options?: { value: string; label: string }[];
  /** Saisi à la création seulement (code, rattachement). */
  immutable?: boolean;
  /** Valeur vide envoyée comme `null` (sinon omise). */
  nullable?: boolean;
  dir?: 'ltr' | 'rtl';
}

type Row = { id: string } & Record<string, unknown>;

function toBody(fields: FieldSpec[], form: Record<string, string>, creating: boolean) {
  const body: Record<string, unknown> = {};
  const put = (key: string, value: unknown) => {
    const [head, sub] = key.split('.') as [string, string | undefined];
    if (!sub) body[head] = value;
    else body[head] = { ...((body[head] as Record<string, unknown>) ?? {}), [sub]: value };
  };
  for (const f of fields) {
    if (f.immutable && !creating) continue;
    const raw = (form[f.key] ?? '').trim();
    if (raw === '') {
      if (f.nullable) put(f.key, null);
      continue;
    }
    put(
      f.key,
      f.type === 'number' || f.type === 'notice'
        ? Number(raw)
        : f.type === 'boolean'
          ? raw === 'true'
          : raw,
    );
  }
  return body;
}

function EntityForm({
  fields,
  initial,
  creating,
  schema,
  onSubmit,
  pending,
  error,
  onCancel,
}: {
  fields: FieldSpec[];
  initial: Record<string, string>;
  creating: boolean;
  schema: z.ZodType;
  onSubmit: (body: unknown) => void;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
}) {
  const t = useT();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = schema.safeParse(toBody(fields, form, creating));
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    onSubmit(parsed.data);
  }

  const control = (f: FieldSpec): ReactNode => {
    const common = {
      value: form[f.key] ?? '',
      disabled: f.immutable && !creating,
      onChange: (e: { target: { value: string } }) =>
        setForm((s) => ({ ...s, [f.key]: e.target.value })),
      ...(f.dir ? { dir: f.dir } : {}),
    };
    if (f.type === 'select' || f.type === 'boolean') {
      const options =
        f.type === 'boolean'
          ? [
              { value: '', label: t.settings.inherited },
              { value: 'true', label: t.common.yes },
              { value: 'false', label: t.common.no },
            ]
          : (f.options ?? []);
      return (
        <Select {...common}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    }
    if (f.type === 'textarea') return <Textarea {...common} />;
    return <Input {...common} type={f.type === 'number' ? 'number' : 'text'} />;
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2" noValidate>
      {fields.map((f) =>
        f.type === 'notice' ? (
          <div key={f.key}>
            <NoticeField
              label={f.label}
              {...(f.none ? { none: f.none } : {})}
              {...(f.hint ? { hint: f.hint } : {})}
              value={form[f.key] ? Number(form[f.key]) : null}
              onChange={(days) =>
                setForm((s) => ({ ...s, [f.key]: days === null ? '' : String(days) }))
              }
            />
            {errors[f.key] ? <p className="text-xs text-destructive">{errors[f.key]}</p> : null}
          </div>
        ) : (
          <Field
            key={f.key}
            label={f.label}
            {...(f.hint ? { hint: f.hint } : {})}
            error={errors[f.key]}
            className={f.type === 'textarea' ? 'sm:col-span-2' : undefined}
          >
            {control(f) as React.ReactElement<Record<string, unknown>>}
          </Field>
        ),
      )}
      <div className="sm:col-span-2">
        <ActionError error={error} />
      </div>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button variant="ghost" onClick={onCancel}>
          {t.common.cancel}
        </Button>
        <Button type="submit" disabled={pending}>
          {t.common.save}
        </Button>
      </div>
    </form>
  );
}

export function CrudSection<T extends Row>({
  title,
  path,
  columns,
  fields,
  createSchema,
  patchSchema,
  toForm,
  deletable = true,
}: {
  title: string;
  path: string;
  columns: { label: string; render: (row: T) => ReactNode }[];
  fields: FieldSpec[];
  createSchema: z.ZodType;
  patchSchema: z.ZodType;
  toForm: (row: T | null) => Record<string, string>;
  deletable?: boolean;
}) {
  const t = useT();
  const api = useApi();
  const admin = useAuth().can('administer');
  const query = useApiQuery<{ items: T[] }>(path);
  const [editing, setEditing] = useState<T | 'new' | null>(null);
  const target = editing === 'new' ? null : editing;
  const save = useApiMutation<unknown>(
    editing === 'new' ? 'POST' : 'PATCH',
    editing === 'new' || !editing ? path : `${path}/${editing.id}`,
    {
      invalidate: [path],
      onSuccess: () => setEditing(null),
    },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {admin ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            {t.common.create}
          </Button>
        ) : null}
      </CardHeader>
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <THead>
                <tr>
                  {columns.map((c) => (
                    <TH key={c.label}>{c.label}</TH>
                  ))}
                  {admin ? (
                    <TH>
                      <span className="sr-only">{t.common.actions}</span>
                    </TH>
                  ) : null}
                </tr>
              </THead>
              <TBody>
                {data.items.map((row) => (
                  <TR key={row.id}>
                    {columns.map((c) => (
                      <TD key={c.label}>{c.render(row)}</TD>
                    ))}
                    {admin ? (
                      <TD className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                          {t.common.edit}
                        </Button>
                        {deletable ? (
                          <ConfirmButton
                            label={t.common.delete}
                            title={t.common.delete}
                            message={String(columns[0]?.render(row) ?? '')}
                            variant="destructive"
                            onConfirm={async () => {
                              await api.request('DELETE', `${path}/${row.id}`);
                              await query.refetch();
                            }}
                          />
                        ) : null}
                      </TD>
                    ) : null}
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        title={editing === 'new' ? `${title} : ${t.common.create}` : `${title} : ${t.common.edit}`}
      >
        {editing !== null ? (
          <EntityForm
            key={target?.id ?? 'new'}
            fields={fields}
            initial={toForm(target)}
            creating={editing === 'new'}
            schema={editing === 'new' ? createSchema : patchSchema}
            onSubmit={(body) => save.mutate(body)}
            pending={save.isPending}
            error={save.error}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}
